import React from 'react'

import { Button, Tooltip } from 'components/ds'

import { ADMIN_ONLY_MESSAGE, PRICING_PAGE_ERROR_MESSAGE } from './utils'

type Props = {
  /** Chargebee pricing page, or with `contactSales` the sales mailto; until it arrives the button shows as loading */
  url?: string
  isAdmin: boolean
  /** The pricing page couldn't be loaded */
  disabled?: boolean
  /** Emails sales instead, for plans that can't change self-serve; open to everyone, admins or not */
  contactSales?: boolean
  /** Called when the link opens Chargebee, by click or middle-click */
  onOpen?: () => void
}

/**
 * Links to the Chargebee pricing page in a new tab, where the subscription is changed,
 * or with `contactSales`, emails sales.
 */
const ManageSubscriptionButton = ({
  url,
  isAdmin,
  disabled,
  contactSales = false,
  onOpen,
}: Props): React.ReactNode => {
  const label = contactSales ? 'Contact sales' : 'Manage subscription'
  const isUsable = contactSales || (isAdmin && !disabled)
  const isLink = isUsable && Boolean(url)

  const button = (
    <Button
      color="gray"
      highContrast
      size="2"
      asChild={isLink}
      loading={isUsable && !url}
      // Explicit, because passing false would override Radix's disabled-while-loading default
      disabled={isUsable && !url}
      // aria-disabled instead of disabled: a natively disabled button swallows the pointer events its tooltip needs
      aria-disabled={!isUsable || undefined}
      data-disabled={!isUsable || undefined}
    >
      {isLink ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onOpen}
          onAuxClick={onOpen}
        >
          {label}
        </a>
      ) : (
        label
      )}
    </Button>
  )

  if (isUsable) return button

  return (
    <Tooltip
      content={isAdmin ? PRICING_PAGE_ERROR_MESSAGE : ADMIN_ONLY_MESSAGE}
    >
      {button}
    </Tooltip>
  )
}

export default ManageSubscriptionButton
